import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation } from '@tanstack/react-query';

import { HexColor } from '@nx-smartmonkey/shared/domain';
import { RoutalDefaultColors } from '@nx-smartmonkey/ui';
import { supervisorService } from '../../application/SupervisorService';
import { Supervisor } from '../../domain/supervisor/Supervisor';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';
import { useRetrieveUser } from '../user/useRetrieveUser';
import { useRetrieveAllSupervisors } from './useRetrieveAllSupervisors';

export interface UseCreateSupervisorProps {
  email: string;
  short_id: string;
  name: string;
  company: string;
  phone: string;
}

type UseCreateSupervisorResponse = ServiceResponse<
  ({ email, short_id, name, company, phone }: UseCreateSupervisorProps) => void,
  void
>;

// Hooks act as 'Adapter' layer.
export const useCreateSupervisor = (): UseCreateSupervisorResponse => {
  const { data: user } = useRetrieveUser();
  const { data: activeProject } = useRetrieveActiveProject();
  const { run: refetchSupervisors, data: projectSupervisors } = useRetrieveAllSupervisors();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    async ({ email, short_id, name, company, phone }: UseCreateSupervisorProps): Promise<Supervisor[]> => {
      // Get the existing vehicles colors.
      const supervisorsColors = (projectSupervisors ?? [])
        .map((supervisor) => (supervisor.color?.value ?? ``).toLowerCase())
        .filter((color) => color !== undefined);

      // Filter the available colors that are already not used for old vehicles.
      const sampleColors = RoutalDefaultColors.filter(
        (defaultColor) => !supervisorsColors.includes(defaultColor.toLowerCase())
      );

      // Setting the supervisor color: the new supervisor color should be one of the sample colors. If sample colors is empty, picks one of the RoutalDefaultColors
      const colorToUse = sampleColors.length > 0 ? sampleColors[0] : RoutalDefaultColors[0];

      const supervisor = Supervisor.create({
        project_id: activeProject!.id,
        organization_id: activeProject!.organization_id,
        email,
        short_id,
        name,
        company,
        phone,
        color: HexColor.create(colorToUse),
        language: user?.language,
        stop_fields: [],
        stop_completed_fields: [],
        stop_canceled_fields: [],
        survey_fields: [],
        created_by: user?.id,
      });

      return supervisorService.createSupervisors({ supervisors: [supervisor], project: activeProject! });
    },
    {
      onSuccess: () => {
        refetchSupervisors();
      },
      onError: (err: any) => {
        console.info(`Create Supervisor Error: `, err?.response);
        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    run: ({ email, short_id, name, company, phone }: UseCreateSupervisorProps) =>
      mutate({ email, short_id, name, company, phone }),
  };
};
